import type { ExtendedMessage } from '@/components/PropertySearchChat';
import { clearGeoSession } from '@/lib/geoSearchApi';

const STORAGE_KEY_PREFIX = 'assistant_chat_state';
const LEGACY_STORAGE_KEY = 'assistant_chat_state';
const GUEST_OWNER = 'guest';

export type PersistedAssistantChatState = {
    version: 1;
    ownerUserId: string;
    messages: ExtendedMessage[];
    sessionId: string | null;
    /** Landing `?q=` already auto-sent — do not resend on remount. */
    consumedLandingQuery: string | null;
};

function ownerKey(userId?: string | null): string {
    return userId?.trim() || GUEST_OWNER;
}

function prefixedKey(userId?: string | null): string {
    return `lmi_${STORAGE_KEY_PREFIX}:${ownerKey(userId)}`;
}

function legacyPrefixedKey(): string {
    return `lmi_${LEGACY_STORAGE_KEY}`;
}

function readRaw(userId?: string | null): PersistedAssistantChatState | null {
    try {
        const raw = sessionStorage.getItem(prefixedKey(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PersistedAssistantChatState;
        if (parsed?.version !== 1 || !Array.isArray(parsed.messages)) return null;
        if (parsed.ownerUserId !== ownerKey(userId)) return null;
        return parsed;
    } catch {
        return null;
    }
}

export function loadAssistantChatState(userId?: string | null): PersistedAssistantChatState | null {
    const state = readRaw(userId);
    if (!state || state.messages.length < 2) return null;
    return state;
}

export function saveAssistantChatState(
    messages: ExtendedMessage[],
    sessionId: string | null,
    consumedLandingQuery: string | null,
    userId?: string | null,
): void {
    try {
        const payload: PersistedAssistantChatState = {
            version: 1,
            ownerUserId: ownerKey(userId),
            messages,
            sessionId,
            consumedLandingQuery,
        };
        sessionStorage.setItem(prefixedKey(userId), JSON.stringify(payload));
    } catch {
        // Quota or private mode — ignore
    }
}

export function clearAssistantChatState(userId?: string | null): void {
    try {
        sessionStorage.removeItem(prefixedKey(userId));
    } catch {
        // ignore
    }
    clearGeoSession();
}

/** Remove assistant chat blobs for every owner except the active user (same browser tab). */
export function clearAssistantChatStatesForOtherUsers(keepUserId: string): void {
    const keepOwner = ownerKey(keepUserId);
    clearLegacyAssistantChatState();
    try {
        const toRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (!key?.startsWith(`lmi_${STORAGE_KEY_PREFIX}:`)) continue;
            const suffix = key.slice(`lmi_${STORAGE_KEY_PREFIX}:`.length);
            if (suffix !== keepOwner) toRemove.push(key);
        }
        toRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
        // ignore
    }
}

export function clearAllAssistantChatStates(): void {
    clearLegacyAssistantChatState();
    clearGeoSession();
    try {
        const toRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key?.startsWith(`lmi_${STORAGE_KEY_PREFIX}`)) toRemove.push(key);
        }
        toRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {
        // ignore
    }
}

function clearLegacyAssistantChatState(): void {
    try {
        sessionStorage.removeItem(legacyPrefixedKey());
    } catch {
        // ignore
    }
}

/** True when URL landing query was already sent for this user in this browser tab session. */
export function landingQueryAlreadyConsumed(
    query: string | undefined,
    userId?: string | null,
): boolean {
    if (!query) return false;
    const state = readRaw(userId);
    return state?.consumedLandingQuery === query && state.messages.length >= 2;
}

/**
 * Home / deep-link search should not resume the previous assistant chat.
 * Landing always sends `q` (and `fresh=1`); either flag means start clean.
 */
export function shouldStartFreshAssistantSearch(searchParams: URLSearchParams): boolean {
    if (searchParams.get('fresh') === '1') return true;
    return Boolean(searchParams.get('q')?.trim());
}
